import { useSite } from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import {
  AllowStringForNumbers,
  CostInputsInrange,
  FinancialModelInRangeLease,
  RecursivePartial,
  Site,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import { SiteCalculationsProvider } from "@inrange/shared-components";
import { Modal, ModalView } from "@inrange/theme-components";
import hash from "object-hash";
import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { applySiteUpdate } from "../site/site-utils";
import FinancialPerformanceInRange from "../site/siteSections/FinancialPerformanceInRange";
import FinancialPerformanceSpvOutputs from "../site/siteSections/FinancialPerformanceSpvOutputs";

const BulkEditFinancialPerformanceInRange = ({
  organisation,
  selectedSiteIds,
  onSitesUpdate,
  onClose,
}: {
  organisation?: Organisation;
  selectedSiteIds: Set<string>;
  onSitesUpdate: (payload: any) => void;
  onClose: () => void;
}) => {
  const [saveNote, setSaveNote] = useState("");
  const { previewMultisiteSpv } = useSite({ app: "admin" });
  const [previewMultisiteSpvError, setPreviewMultisiteSpvError] =
    useState(false);
  const [site, setSite] = useState<
    RecursivePartial<
      AllowStringForNumbers<{
        id: string;
        costInputsInrange: CostInputsInrange;
      }>
    >
  >({
    id: "bulk-preview",
    costInputsInrange: {},
  });
  const [multisiteSpvResults, setMultisiteSpvResults] = useState<{
    financialModelsInrange: { lease: Partial<FinancialModelInRangeLease> };
  }>({
    financialModelsInrange: { lease: {} },
  });
  useEffect(() => {
    if (previewMultisiteSpv.isSuccess) {
      setPreviewMultisiteSpvError(false);
      setMultisiteSpvResults((prevCalculations) => ({
        ...prevCalculations,
        ...previewMultisiteSpv.data,
      }));
      previewMultisiteSpv.reset();
    }

    if (previewMultisiteSpv.isError) {
      setPreviewMultisiteSpvError(true);
      previewMultisiteSpv.reset();
    }
  }, [previewMultisiteSpv]);

  const [previousHash, setPreviousHash] = useState<string>("initial-hash");

  useEffect(() => {
    const costInputsInrange = site.costInputsInrange || {};
    const updatedSiteHash = hash(costInputsInrange);
    if (previousHash === updatedSiteHash) return;

    const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;

    // Debounce so we don't recalculate immediately on keypress
    const debounceWaitMillis = testMode ? 0 : 300;
    const timeoutId = setTimeout(() => {
      setPreviousHash(updatedSiteHash);
      previewMultisiteSpv.mutate({
        costInputsInrange,
        siteIds: Array.from(selectedSiteIds),
      });
    }, debounceWaitMillis);

    return () => clearTimeout(timeoutId);
  }, [
    previousHash,
    site.costInputsInrange,
    selectedSiteIds,
    previewMultisiteSpv,
  ]);
  return (
    <Modal>
      <ModalView
        id="bulk-edit-financial-performance-in-range"
        title={`Edit InRange financial performance for ${selectedSiteIds.size} site${selectedSiteIds.size === 1 ? "" : "s"}`}
        fontWeight="500"
        fontSize="20px"
        width="90vw"
        maxHeight="100%"
        titlePadding="0 0 10px 0"
      >
        <SiteCalculationsProvider
          site={site as unknown as Partial<Site>}
          originalSite={{}}
          app="admin"
          isBulkEdit={true}
        >
          <FinancialPerformanceInRange
            site={site as unknown as SiteAllowStringValues}
            setSite={(update) => {
              setSite((prevSite) =>
                applySiteUpdate(prevSite, update, [], true)
              );
            }}
            isOwnerOccupied={false}
            isBulkEdit={true}
            bulkEditSiteIds={selectedSiteIds}
            bulkEditSpvReportPrefixName={
              organisation ? `org-${organisation.name}` : "sites"
            }
          />
          <FinancialPerformanceSpvOutputs
            loading={previewMultisiteSpv.isLoading}
            error={previewMultisiteSpvError}
            costInputsInrange={site.costInputsInrange || {}}
            inrangeFinancialPerformanceLease={
              multisiteSpvResults.financialModelsInrange.lease
            }
          />
          <Form.Control
            className="mt-3"
            as="textarea"
            rows={2}
            placeholder="Add a short note describing the change made"
            value={saveNote}
            onChange={(e) => setSaveNote(e.target.value)}
            autoFocus
            data-testid="site-save-notes"
          />
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              flexDirection: "row",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Button variant="inrangesecondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                const updates = Array.from(selectedSiteIds).reduce(
                  (acc, siteId) => {
                    acc[siteId] = { site };
                    if (saveNote.trim().length > 0) {
                      acc[siteId].userNote = saveNote.trim();
                    }
                    return acc;
                  },
                  {} as { [key: string]: any }
                );
                onSitesUpdate(updates);
              }}
            >
              Edit InRange financial performance
            </Button>
          </div>
        </SiteCalculationsProvider>
      </ModalView>
    </Modal>
  );
};

export default BulkEditFinancialPerformanceInRange;
